import React from "react";
import * as yup from "yup";
import { SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import { getFixedT } from "i18next";
import { Validation } from "../../localization/localeValidation";
import ajaxClient from "../../../../js/common/AjaxClient";

interface CheckLoginResponse {
    readonly isValid: boolean;
}

// eslint-disable-next-line unicorn/no-null
const fixedT = getFixedT<"header">(null, "header");

const formSchema = yup.object({
    email: yup.string()
        .lowercase()
        .trim()
        .required(fixedT("emailRequired"))
        .matches(Validation.EmailRegEx, fixedT("validEmail")),
    password: yup.string()
        .required(fixedT("passwordRequired"))
        .min(5, fixedT("minCharacters")),
});

export type LoginFormData = yup.InferType<typeof formSchema>;

const sendUserLogin = async function(data: LoginFormData): Promise<boolean> {
    const response = await ajaxClient.post<CheckLoginResponse>("/Customer/jsCheckLogin", {
        email: data.email,
        password: data.password,
    });

    return response.isValid;
};

export default function LoginForm(): React.JSX.Element {
    const { t } = useTranslation("header");
    const [invalidLogin, setInvalidLogin] = React.useState<boolean>(false);

    const { register, handleSubmit, formState: { errors } } = useForm<LoginFormData>({
        resolver: yupResolver(formSchema),
        defaultValues: {
            email: "@",
        },
    });
    const onSubmit: SubmitHandler<LoginFormData> = async (data: LoginFormData, event): Promise<void> => {
        setInvalidLogin(false);
        const loginSuccess = await sendUserLogin(data);

        if (!loginSuccess) {
            setInvalidLogin(true);

            return;
        }

        if (event?.target instanceof HTMLFormElement) {
            event.target.submit();
        }
    };

    return (
        <form action="/Customer/login" method="post" onSubmit={(event): void => void handleSubmit(onSubmit)(event)}>
            <div className="form-group">
                <label htmlFor="login_email">{t("email")}</label>
                <input
                    {...register("email", { onChange: (): void => setInvalidLogin(false) })} id="login_email"
                    className="form-control" autoComplete="username" />
                <label htmlFor="login_email" className="error">{errors.email?.message}</label>
            </div>

            <div className="form-group">
                <label htmlFor="login_password">{t("password")}</label>
                <input
                    type="password" {...register("password", { onChange: (): void => setInvalidLogin(false) })}
                    id="login_password" className="form-control" autoComplete="current-password" />
                <label htmlFor="login_password" className="error">{errors.password?.message}</label>
                {invalidLogin && <label htmlFor="login_password" className="error">{t("invalidLogin")}</label>}
            </div>
            <input type="hidden" name="loginCallbackLocation" value={globalThis.location.href} />

            <div className="buttons">
                <button type="submit" className="btn btn-success">{t("login")}</button>
            </div>
        </form>
    );
}
